import { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';

import { checkout as checkoutMutation } from '../../../../../graphql/mutations/checkout';
import {
  EventItems,
  MeForPurchasedEvents,
} from '../../../../../graphql/queries/events';
import { sentryCaptureMessage } from '../../../../../util/sentry';
import { MeQuery } from '../../../../../graphql/queries/account';
import useAuthenticated from '../../../../../hooks/useAuthenticated';

/**
 * Photo search checkout hook.
 *
 * @param {Object} options The options.
 * @param {Object?} options.account The Account object.
 * @param {Object} options.albumFolderId An albumFolderId with an existing purchased event. Used for upgrading.
 * @param {Function} [options.onCheckoutSuccess] Method running on checkout success.
 * @param {Function} [options.onCheckoutError] Method running on checkout error.
 * @returns {{onCheckout: Function, submitting: boolean, checkoutError: string, success: boolean, purchasedEvents: {{ records: array, total: number }}, eventItems: array, loadingEventItems: boolean, loadingPurchasedEvents: boolean }}
 */
const usePurchaseEventCheckout = ({
  account,
  onCheckoutSuccess,
  onCheckoutError,
  albumFolderId,
} = {}) => {
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [success, setSuccess] = useState(false);

  const { data: { eventItems = [] } = {}, loading: loadingEventItems } =
    useQuery(EventItems);

  const {
    data: { me: { purchased_events: purchasedEvents = {} } = {} } = {},
    loading: loadingPurchasedEvents,
  } = useQuery(MeForPurchasedEvents, {
    fetchPolicy: 'cache-and-network',
    variables: {
      album_folder_id: albumFolderId,
      unassigned_only: !albumFolderId,
    },
    skip: !authenticated,
  });

  const [checkout] = useMutation(checkoutMutation, {
    refetchQueries: [
      {
        query: MeForPurchasedEvents,
        variables: {
          album_folder_id: albumFolderId,
          unassigned_only: !albumFolderId,
        },
      },
    ],
  });

  const onCheckout = async ({ token, eventItemId, quantity = 1 } = {}) => {
    if (submitting) {
      return;
    }

    const eventItem = (eventItems ?? []).find(item => item.id === eventItemId);

    try {
      setSubmitting(true);
      setCheckoutError('');

      // Tokens may not have IDs on free checkouts
      if (eventItemId) {
        await checkout({
          variables: {
            input: {
              account_id: account?.id ?? user?.id,
              stripe_token: token?.id,
              stripe_payment_method_id: token?.stripe_payment_method_id,
              stripe_connect_account_id: token?.stripe_connect_account_id,
              email_address:
                token?.email ?? account?.email_address ?? user?.email_address,
              promo_code: token?.promo_code,
              event_items: [
                {
                  event_item_id: eventItemId,
                  quantity,
                  album_folder_id: albumFolderId,
                },
              ],
              platform: 'WEB',
            },
          },
        });
      }

      setSubmitting(false);
      setSuccess(true);
      onCheckoutSuccess?.({ eventItem });
    } catch (error) {
      setCheckoutError(error);
      onCheckoutError?.(error);
      sentryCaptureMessage(error, { level: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    checkoutError,
    submitting,
    success,
    onCheckout,
    eventItems,
    loadingEventItems,
    purchasedEvents,
    loadingPurchasedEvents,
  };
};

export default usePurchaseEventCheckout;
